import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Slider,
} from '@mui/material';
import client from '../../utils/contentfulClient';
import { toTitleCase } from '../../utils/casing';

const RadioSelect =
  'https://assets.askhippo.co.za/Icons/common/radio-select.svg';
const RadioDeselect =
  'https://assets.askhippo.co.za/Icons/common/radio-deselect.svg';

const UncheckedRadioIcon = () => (
  <img
    src={RadioDeselect}
    alt="Unchecked"
    style={{ width: 15, height: 15, display: 'block' }}
  />
);
const CheckedRadioIcon = () => (
  <img
    src={RadioSelect}
    alt="Checked"
    style={{ width: 15, height: 15, display: 'block' }}
  />
);

const optionLabelSx = {
  fontFamily: 'Inter',
  fontWeight: 500,
  fontSize: '0.688rem',
  lineHeight: '22px',
  color: '#1E354E',
};

const Divider = () => (
  <Box
    sx={{
      width: 'calc(100% + 56px)',
      height: '1px',
      backgroundColor: '#E0E0E0',
      my: 1.5,
      ml: '-16px',
    }}
  />
);

const BlogSidebarMobile = ({
  activeCategory = 'all',
  activeYear = null,
  onClose,
}) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [dates, setDates] = useState([]);
  const [yearSliderIndex, setYearSliderIndex] = useState(0);

  const hasActiveFilters = activeCategory !== 'all' || Boolean(activeYear);

  // Oldest -> newest, left to right, for the year slider.
  const sortedYears = [...dates].sort();

  useEffect(() => {
    if (!sortedYears.length) return;

    const activeIndex = activeYear ? sortedYears.indexOf(activeYear) : -1;
    setYearSliderIndex(activeIndex >= 0 ? activeIndex : sortedYears.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates.join(','), activeYear]);

  useEffect(() => {
    let isMounted = true;

    const getSideBarFilters = async () => {
      try {
        const response = await client.getEntries({
          content_type: 'blogPost',
          order: '-fields.blogDate',
          select: ['fields.category', 'fields.blogDate'],
          limit: 1000,
        });

        const finalCategories = [
          ...new Set(
            response.items.map((item) => item.fields?.category).filter(Boolean)
          ),
        ];

        const finalYearValues = [
          ...new Set(
            response.items
              .map((item) => item.fields?.blogDate?.substring(0, 4))
              .filter(Boolean)
          ),
        ];

        if (!isMounted) return;

        setDates(finalYearValues.sort().reverse());

        finalCategories.push('all');
        setCategories(finalCategories.sort());
      } catch (err) {
        // ignore
      }
    };

    getSideBarFilters();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#EDEDEE',
          px: '19px',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '0.875rem',
            lineHeight: '24px',
            letterSpacing: '-0.002em',
            color: '#1C2F3A',
            my: '15px',
          }}
        >
          Filter Blog Posts
        </Typography>

        <Box
          onClick={onClose}
          sx={{
            width: 40,
            height: 40,
            borderRadius: '16px',
            backgroundColor: '#D9D9D9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <span
            className="material-symbols-rounded"
            style={{ fontSize: '1.5rem', color: '#162F3B' }}
          >
            close
          </span>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          pl: '16px',
          pr: '40px',
          pt: 2,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <RadioGroup
          value={activeCategory !== 'all' ? `/blog/${activeCategory}` : ''}
          onChange={(e) => {
            navigate(e.target.value);
            onClose?.();
          }}
          sx={{ width: '100%', maxWidth: '100%' }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '0.813rem',
                lineHeight: '28px',
                color: '#1C2F3A',
                flexShrink: 0,
              }}
            >
              Categories
            </Typography>
            {hasActiveFilters && (
              <Typography
                onClick={() => {
                  navigate('/blog/all');
                  onClose?.();
                }}
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  lineHeight: '28px',
                  letterSpacing: '-0.002em',
                  color: '#A2ACB1',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': { opacity: 0.9 },
                }}
              >
                Clear filters
              </Typography>
            )}
          </Box>
          {categories
            .filter(
              (category) => !(category === 'all' && activeCategory === 'all')
            )
            .map((category) => (
              <FormControlLabel
                key={category}
                value={`/blog/${category}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  mb: '12px',
                  ml: 0,
                  mr: 0,
                }}
                control={
                  <Radio
                    icon={<UncheckedRadioIcon />}
                    checkedIcon={<CheckedRadioIcon />}
                    sx={{ p: 0, mr: '11px' }}
                  />
                }
                label={
                  <Typography sx={optionLabelSx}>
                    {toTitleCase(category)}
                  </Typography>
                }
              />
            ))}
        </RadioGroup>

        <Divider />
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: '0.813rem',
            lineHeight: '28px',
            color: '#1C2F3A',
            mb: 1,
          }}
        >
          Archive
        </Typography>
        {sortedYears.length > 1 && (
          <Box sx={{ mt: 1.5, mb: '12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '0.688rem',
                  lineHeight: '20px',
                  color: '#1C2F3A',
                }}
              >
                {sortedYears[yearSliderIndex]}
              </Typography>
            </Box>
            <Box sx={{ px: '7.5px' }}>
              <Slider
                min={0}
                max={sortedYears.length - 1}
                step={1}
                value={yearSliderIndex}
                onChange={(_, val) => setYearSliderIndex(val)}
                onChangeCommitted={(_, val) => {
                  const year = sortedYears[val];
                  if (year) {
                    navigate(`/blog/SortByYear/?Year=${year}`);
                    onClose?.();
                  }
                }}
                sx={{
                  color: '#C9CC37',
                  height: 4,
                  '& .MuiSlider-thumb': {
                    width: 15,
                    height: 15,
                    backgroundColor: '#C9CC37',
                    boxShadow: 'none',
                  },
                  '& .MuiSlider-track': {
                    height: 2,
                    border: 'none',
                    ml: '-7.5px',
                    mr: '7.5px',
                  },
                  '& .MuiSlider-rail': {
                    height: 2,
                    backgroundColor: '#C9CC37',
                    ml: '7.5px',
                    mr: '-7.5px',
                  },
                }}
              />
            </Box>
          </Box>
        )}
        {sortedYears.length === 1 && (
          <Typography
            onClick={() => {
              navigate(`/blog/SortByYear/?Year=${sortedYears[0]}`);
              onClose?.();
            }}
            sx={{
              ...optionLabelSx,
              display: 'block',
              mb: '12px',
              cursor: 'pointer',
            }}
          >
            {sortedYears[0]}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BlogSidebarMobile;
